/**
 * GOVERNANÇA DOCUMENTAL — POLÍTICA ÚNICA DE APROVAÇÃO PELO PLH.
 *
 * O fluxo
 *
 *   INICIAR ANÁLISE → ENVIAR PARA CORREÇÃO → REANALISAR → APROVAR RELATÓRIO
 *
 * é TRANSVERSAL: não pertence a Integridade, nem a END, nem à AVR. Este
 * arquivo é a única fonte da máquina de estados, da matriz de ações e do
 * critério de autorização; os módulos declaram o documento e consultam.
 *
 * POR QUE ELE EXISTE
 *
 * A mesma regra estava escrita três vezes, em três lugares que podiam
 * divergir sem ninguém notar:
 *
 *   END (LP, ME, PM, USC)  `TechnicalReviewService` + `TechnicalDocumentReview`
 *   Integridade            `ReportService.sendToPlh/returnByPlh/approveByPlh`
 *   AVR                    `AvrReportService.plhReview` (start/aprovar/corrigir)
 *
 * Cada um com o seu enum e as suas condições. Um ajuste na regra de quem pode
 * aprovar precisava ser lembrado em três arquivos, e esquecer um deles não
 * quebra teste nenhum — só deixa um caminho de aprovação mais fraco que os
 * outros. Foi assim que o ME passou meses conferindo o Cliente da SESSÃO
 * enquanto os irmãos conferiam o Cliente do DOCUMENTO.
 *
 * Este módulo é PURO: sem repositório, sem I/O, sem `window`. Quem persiste é
 * cada serviço; quem decide é aqui.
 */

import type { TechnicalDocumentType } from "./technical-document-review.js";

/* ══════════════════════════════════════════════════════════════════════
   1 — ESTADOS GLOBAIS
   ══════════════════════════════════════════════════════════════════════ */

/**
 * Os estados do ciclo de aprovação, iguais para todo tipo de documento.
 *
 * `AGUARDANDO_REANALISE_PLH` é distinto de `AGUARDANDO_ANALISE_PLH` de
 * propósito: são a mesma espera, mas não a mesma história. Colapsar os dois
 * apagaria do painel a informação de que aquele documento já voltou uma vez
 * — que é justamente o que faz alguém olhá-lo com mais atenção.
 */
export type PlhApprovalStatus =
  | "DRAFT"
  | "EM_ELABORACAO"
  | "AGUARDANDO_ANALISE_PLH"
  | "EM_ANALISE_PLH"
  | "CORRECAO_SOLICITADA"
  | "AGUARDANDO_REANALISE_PLH"
  | "APROVADO_PLH";

export const PLH_APPROVAL_STATUS_LABELS: Record<PlhApprovalStatus, string> = {
  DRAFT: "Rascunho",
  EM_ELABORACAO: "Em Elaboração",
  AGUARDANDO_ANALISE_PLH: "Aguardando Análise do PLH",
  EM_ANALISE_PLH: "Em Análise do PLH",
  CORRECAO_SOLICITADA: "Correção Solicitada",
  AGUARDANDO_REANALISE_PLH: "Aguardando Reanálise do PLH",
  APROVADO_PLH: "Aprovado pelo PLH"
};

export function isPlhApprovalStatus(value: unknown): value is PlhApprovalStatus {
  return typeof value === "string" && value in PLH_APPROVAL_STATUS_LABELS;
}

/* ══════════════════════════════════════════════════════════════════════
   2 — AÇÕES E MATRIZ
   ══════════════════════════════════════════════════════════════════════ */

export type PlhApprovalAction =
  | "START_ANALYSIS"
  | "REQUEST_CORRECTION"
  | "APPROVE"
  | "RESUBMIT"
  | "EDIT"
  | "VIEW";

export const PLH_APPROVAL_ACTION_LABELS: Record<PlhApprovalAction, string> = {
  START_ANALYSIS: "INICIAR ANÁLISE",
  REQUEST_CORRECTION: "ENVIAR PARA CORREÇÃO",
  APPROVE: "APROVAR RELATÓRIO",
  RESUBMIT: "ENVIAR NOVAMENTE PARA ANÁLISE",
  EDIT: "EDITAR",
  VIEW: "CONSULTAR"
};

/** Quem age. Não é o perfil do usuário: é o papel dele NESTE documento. */
export type PlhApprovalRole = "PLH" | "ELABORATOR";

/**
 * A matriz de ações, estado por papel. É a tabela do comando, em código —
 * para que a tela e o servidor não possam discordar sobre o que é possível.
 */
const ACTION_MATRIX: Record<PlhApprovalStatus, Record<PlhApprovalRole, readonly PlhApprovalAction[]>> = {
  DRAFT: {
    PLH: ["VIEW"],
    ELABORATOR: ["EDIT"]
  },
  EM_ELABORACAO: {
    PLH: ["VIEW"],
    ELABORATOR: ["EDIT"]
  },
  AGUARDANDO_ANALISE_PLH: {
    PLH: ["START_ANALYSIS"],
    ELABORATOR: ["VIEW"]
  },
  EM_ANALISE_PLH: {
    PLH: ["REQUEST_CORRECTION", "APPROVE"],
    ELABORATOR: ["VIEW"]
  },
  CORRECAO_SOLICITADA: {
    PLH: ["VIEW"],
    ELABORATOR: ["EDIT", "RESUBMIT"]
  },
  AGUARDANDO_REANALISE_PLH: {
    PLH: ["START_ANALYSIS"],
    ELABORATOR: ["VIEW"]
  },
  // Aprovado: ninguém edita. Alterar exige nova revisão.
  APROVADO_PLH: {
    PLH: ["VIEW"],
    ELABORATOR: ["VIEW"]
  }
};

export function availablePlhActions(status: PlhApprovalStatus, role: PlhApprovalRole): readonly PlhApprovalAction[] {
  return ACTION_MATRIX[status][role];
}

export function isActionAllowed(status: PlhApprovalStatus, role: PlhApprovalRole, action: PlhApprovalAction): boolean {
  return availablePlhActions(status, role).includes(action);
}

/** O estado resultante de uma ação — ou null quando a ação não transiciona. */
const TRANSITIONS: Partial<Record<PlhApprovalAction, PlhApprovalStatus>> = {
  START_ANALYSIS: "EM_ANALISE_PLH",
  REQUEST_CORRECTION: "CORRECAO_SOLICITADA",
  APPROVE: "APROVADO_PLH",
  RESUBMIT: "AGUARDANDO_REANALISE_PLH"
};

export function nextStatusFor(action: PlhApprovalAction): PlhApprovalStatus | null {
  return TRANSITIONS[action] ?? null;
}

/**
 * A transição é permitida a partir deste estado, para este papel?
 *
 * É esta função que o SERVIDOR chama. Esconder um botão nunca foi controle:
 * uma chamada direta manda o estado que quiser, e é aqui que ela é recusada.
 */
export function canTransition(
  status: PlhApprovalStatus,
  role: PlhApprovalRole,
  action: PlhApprovalAction
): { allowed: true } | { allowed: false; reason: string } {
  if (!isActionAllowed(status, role, action)) {
    return {
      allowed: false,
      reason: `A ação "${PLH_APPROVAL_ACTION_LABELS[action]}" não é possível com o documento em "${PLH_APPROVAL_STATUS_LABELS[status]}".`
    };
  }
  return { allowed: true };
}

/* ══════════════════════════════════════════════════════════════════════
   3 — BLOQUEIO DA REVISÃO APROVADA
   ══════════════════════════════════════════════════════════════════════ */

/**
 * Estados em que a revisão não aceita edição.
 *
 * `APROVADO_PLH` é imutável: quem precisa alterar cria a PRÓXIMA revisão, e a
 * aprovada permanece intacta. Editar uma revisão já aprovada faria o parecer
 * do PLH passar a valer para um conteúdo que ele não viu.
 *
 * As esperas também bloqueiam — editar enquanto o PLH analisa mudaria o
 * documento debaixo de quem o está lendo.
 */
const LOCKED_STATUSES: readonly PlhApprovalStatus[] = [
  "AGUARDANDO_ANALISE_PLH",
  "EM_ANALISE_PLH",
  "AGUARDANDO_REANALISE_PLH",
  "APROVADO_PLH"
];

export function isRevisionLocked(status: PlhApprovalStatus): boolean {
  return LOCKED_STATUSES.includes(status);
}

/** Bloqueio definitivo: só sai por NOVA REVISÃO, nunca por reabertura. */
export function isRevisionImmutable(status: PlhApprovalStatus): boolean {
  return status === "APROVADO_PLH";
}

export const REVISION_IMMUTABLE_MESSAGE =
  "Esta revisão foi aprovada pelo PLH e não pode mais ser alterada. Para modificar o conteúdo, crie uma nova revisão — a revisão aprovada permanece intacta.";

/* ══════════════════════════════════════════════════════════════════════
   4 — QUAIS DOCUMENTOS PASSAM POR AQUI
   ══════════════════════════════════════════════════════════════════════ */

/**
 * Tipos sujeitos à aprovação do PLH.
 *
 * Um tipo novo entra acrescentando uma linha aqui — não reconstruindo o
 * fluxo. É o `requiresPlhApproval = true` do comando, num lugar só.
 *
 * `BSCAN` já consta: o tipo existe no domínio e o módulo ainda não foi
 * construído. Declará-lo agora evita que, quando for, alguém invente um
 * quarto caminho de aprovação por não achar onde declarar.
 */
const TYPES_REQUIRING_PLH_APPROVAL: ReadonlySet<TechnicalDocumentType> =
  new Set<TechnicalDocumentType>(["INTEGRITY", "LP", "ME", "PM", "USC", "BSCAN", "AVR"]);

export function requiresPlhApproval(documentType: TechnicalDocumentType): boolean {
  return TYPES_REQUIRING_PLH_APPROVAL.has(documentType);
}

export function documentTypesRequiringPlhApproval(): readonly TechnicalDocumentType[] {
  return [...TYPES_REQUIRING_PLH_APPROVAL];
}

/* ══════════════════════════════════════════════════════════════════════
   5 — AUTORIZAÇÃO
   ══════════════════════════════════════════════════════════════════════ */

/**
 * O contexto mínimo para decidir se alguém pode agir como PLH num documento.
 *
 * `documentClientId` é o Cliente A QUE O DOCUMENTO PERTENCE — nunca o Cliente
 * selecionado na sessão. O Cliente da sessão serve para navegar e filtrar; é
 * um valor que a interface troca livremente, e usá-lo como base de
 * autorização foi um defeito real deste sistema (CLAUDE.md, seção 28).
 */
export interface PlhActionRequest {
  /** Professional da conta logada. Null quando a conta não tem um vinculado. */
  readonly actingProfessionalId: string | null | undefined;
  /** PLH que o CLIENTE DO DOCUMENTO nomeou responsável. */
  readonly documentResponsiblePlhProfessionalId: string | null | undefined;
  /** Cliente do documento. Null significa "não resolvido" — e nega. */
  readonly documentClientId: string | null | undefined;
  /** Permissões efetivas da sessão. */
  readonly permissions: readonly string[] | undefined;
  /** A permissão exigida por esta ação específica. */
  readonly requiredPermission: string;
  readonly status: PlhApprovalStatus;
  readonly action: PlhApprovalAction;
  /** O Professional está ativo, é Engenheiro e tem assinatura cadastrada? */
  readonly professionalQualified: boolean;
  readonly professionalQualificationReason?: string;
}

export interface PlhActionDecision {
  readonly allowed: boolean;
  /** Motivo em linguagem de usuário. Vazio quando permitido. */
  readonly reason: string;
}

const DENY = (reason: string): PlhActionDecision => ({ allowed: false, reason });

/**
 * As oito conferências do comando, na ordem, num lugar só.
 *
 * Qualquer uma que não puder ser resolvida NEGA. "Não consegui verificar" não
 * é "pode": num sistema de aprovação técnica, a falha tem de ser fechada.
 */
export function decidePlhAction(request: PlhActionRequest): PlhActionDecision {
  // 1 e 2 — usuário autenticado com Professional vinculado.
  const acting = (request.actingProfessionalId ?? "").trim();
  if (!acting) {
    return DENY("Sua conta não possui um Profissional vinculado. Peça à Administração para vincular em Pessoas e Acessos.");
  }

  // 3 — Cliente do documento identificado. Sem ele não há contra o que conferir.
  const clientId = (request.documentClientId ?? "").trim();
  if (!clientId) {
    return DENY("CLIENTE DO DOCUMENTO NÃO IDENTIFICADO. Não é possível verificar a responsabilidade técnica deste documento.");
  }

  // 4 — o Cliente nomeou alguém?
  const responsible = (request.documentResponsiblePlhProfessionalId ?? "").trim();
  if (!responsible) {
    return DENY("O Cliente deste documento não possui um Engenheiro PLH responsável definido. Configure em Administração → Clientes.");
  }

  // 5 — é ESTA pessoa. Um PLH de outro Cliente é negado aqui.
  if (acting !== responsible) {
    return DENY("Você não é o Engenheiro PLH responsável definido no cadastro do Cliente deste documento.");
  }

  // 6 — habilitação técnica (ativo, Engenheiro, assinatura cadastrada).
  if (!request.professionalQualified) {
    return DENY(request.professionalQualificationReason || "O Profissional não está habilitado a atuar como PLH.");
  }

  // 7 — permissão. Vale a permissão resolvida, nunca o nome do perfil.
  const permissions = request.permissions ?? [];
  if (!permissions.includes(request.requiredPermission)) {
    return DENY("Seu perfil não possui a permissão necessária para esta ação.");
  }

  // 8 — a ação é possível no estado atual.
  const transition = canTransition(request.status, "PLH", request.action);
  if (!transition.allowed) return DENY(transition.reason);

  return { allowed: true, reason: "" };
}

/* ══════════════════════════════════════════════════════════════════════
   6 — TRADUÇÃO DOS ESTADOS DE CADA MÓDULO
   ══════════════════════════════════════════════════════════════════════ */

/**
 * O estado global de um registro do ciclo central (END: LP, ME, PM, USC).
 *
 * `AGUARDANDO_REANALISE_PLH` é deduzido, não guardado: é uma espera que já
 * teve uma correção antes. Guardá-lo como um sexto valor criaria uma segunda
 * fonte para o mesmo fato — o par `correctionRequestedAt`/`correctedAt` já
 * conta essa história, e dois campos que podem discordar é pior que um
 * derivado.
 */
export function plhStatusOfReview(review: {
  status: "AWAITING_PLH_REVIEW" | "PLH_REVIEWING" | "CORRECTION_REQUESTED" | "PLH_APPROVED";
  correctionRequestedAt?: string | null;
}): PlhApprovalStatus {
  switch (review.status) {
    case "PLH_REVIEWING": return "EM_ANALISE_PLH";
    case "CORRECTION_REQUESTED": return "CORRECAO_SOLICITADA";
    case "PLH_APPROVED": return "APROVADO_PLH";
    case "AWAITING_PLH_REVIEW":
      return review.correctionRequestedAt ? "AGUARDANDO_REANALISE_PLH" : "AGUARDANDO_ANALISE_PLH";
  }
}

/**
 * O estado global de uma AVR, a partir do enum próprio do módulo.
 *
 * A AVR mantém o seu ciclo interno; esta função é a ponte, para que o painel
 * e as regras transversais não precisem conhecer `AvrReportStatus`.
 */
export function plhStatusOfAvr(status: string, correctionRequestedBefore = false): PlhApprovalStatus {
  switch (status) {
    case "AWAITING_PLH_REVIEW": return correctionRequestedBefore ? "AGUARDANDO_REANALISE_PLH" : "AGUARDANDO_ANALISE_PLH";
    case "PLH_REVIEWING": return "EM_ANALISE_PLH";
    case "CORRECTION_REQUESTED": return "CORRECAO_SOLICITADA";
    case "PLH_APPROVED":
    case "SIGNED": return "APROVADO_PLH";
    case "DRAFT": return "DRAFT";
    default: return "EM_ELABORACAO";
  }
}

/**
 * O estado global de um Relatório de Integridade, a partir do enum do módulo.
 */
export function plhStatusOfIntegrityReport(status: string, correctionRequestedBefore = false): PlhApprovalStatus {
  switch (status) {
    case "SUBMITTED_TO_ENGINEERING": return correctionRequestedBefore ? "AGUARDANDO_REANALISE_PLH" : "AGUARDANDO_ANALISE_PLH";
    case "UNDER_ENGINEERING_REVIEW": return "EM_ANALISE_PLH";
    case "RETURNED":
    case "IN_CORRECTION": return "CORRECAO_SOLICITADA";
    case "APPROVED":
    case "AWAITING_SIGNATURE":
    case "SIGNED":
    case "COMPLETED":
    case "ISSUED": return "APROVADO_PLH";
    case "DRAFT": return "DRAFT";
    default: return "EM_ELABORACAO";
  }
}

/* ══════════════════════════════════════════════════════════════════════
   7 — HISTÓRICO PADRONIZADO
   ══════════════════════════════════════════════════════════════════════ */

/**
 * Uma entrada do histórico de aprovação, igual para todo tipo de documento.
 *
 * É append-only por contrato: nada aqui é editado nem removido depois de
 * escrito. Um histórico que pode ser reescrito não serve de auditoria.
 */
export interface PlhApprovalHistoryEntry {
  readonly at: string;
  readonly action: PlhApprovalAction;
  readonly documentType: TechnicalDocumentType;
  readonly documentId: string;
  readonly documentNumber: string;
  readonly revision: string;
  readonly previousStatus: PlhApprovalStatus;
  readonly newStatus: PlhApprovalStatus;
  readonly professionalId: string | null;
  readonly professionalName: string;
  readonly userId: string | null;
  readonly clientId: string | null;
  readonly projectId: string | null;
  /** Obrigatório em REQUEST_CORRECTION; opcional nas demais. */
  readonly comment: string;
}

/** Frase do histórico — mesma redação para Integridade, END e AVR. */
export function describeHistoryEntry(entry: PlhApprovalHistoryEntry): string {
  switch (entry.action) {
    case "START_ANALYSIS": return `Análise iniciada pelo PLH ${entry.professionalName}.`;
    case "REQUEST_CORRECTION": return `Correção solicitada pelo PLH ${entry.professionalName}. Comentário: "${entry.comment}"`;
    case "APPROVE": return `Relatório aprovado pelo PLH ${entry.professionalName}.`;
    case "RESUBMIT": return "Relatório reenviado para análise.";
    case "EDIT": return "Relatório editado pelo elaborador.";
    case "VIEW": return "Relatório consultado.";
  }
}

export const CORRECTION_COMMENT_REQUIRED =
  "Informe o motivo da correção. Uma solicitação sem motivo não diz ao elaborador o que corrigir.";

/** O comentário da correção é obrigatório e não pode ser só espaço em branco. */
export function validateCorrectionComment(comment: string | null | undefined): string[] {
  return (comment ?? "").trim() === "" ? [CORRECTION_COMMENT_REQUIRED] : [];
}
